import { motion } from 'framer-motion';
import { ExternalLink, Users } from 'lucide-react';
import { OMS_COLORS } from '../omsDashTheme';
import type { ProjectPerf } from '../utils/performanceModel';
import { formatNumber } from '../utils/formatNumber';
import { StatusBar, StatusColorKey } from './StatusBits';
import ScoreGauge from './ScoreGauge';
import StatCount from './StatCount';

interface Props {
  rows: ProjectPerf[];
  deltaPhrase: string;
  expanded: boolean;
  onExpand: () => void;
  /** Breadcrumb parent (location name) for the "View all" copy. */
  parentName: string;
  compareIds: Set<string>;
  onToggleCompare: (id: string) => void;
  onDrill: (projectId: string) => void;
  onOpenProject: (projectId: string) => void;
}

const PREVIEW_COUNT = 10;

/**
 * Explorer Level 2 — ranked project ledger for one state. Every row states
 * its denominators ("62% complete of 130 · 94% pass on 81 completed") and
 * carries the one status vocabulary via the segmented bar + adjacent legend.
 */
export default function ProjectLeaderboard({
  rows,
  deltaPhrase,
  expanded,
  onExpand,
  parentName,
  compareIds,
  onToggleCompare,
  onDrill,
  onOpenProject,
}: Props) {
  const visible = expanded ? rows : rows.slice(0, PREVIEW_COUNT);
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 pb-1.5">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: OMS_COLORS.caption }}>
          {formatNumber(rows.length)} projects · click a row for its engineers
        </p>
        <StatusColorKey />
      </div>
      <ul className="space-y-1.5">
        {visible.map((row, index) => {
          const completionPct = row.scheduled > 0 ? Math.round((row.completed / row.scheduled) * 100) : null;
          const passPct = row.completed > 0 ? Math.round((row.counts.pass / row.completed) * 100) : null;
          const selected = compareIds.has(row.id);
          return (
            <motion.li
              key={row.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: Math.min(index * 0.03, 0.3), duration: 0.28 }}
              className="group relative"
            >
              <button
                type="button"
                data-perf-row
                onClick={() => onDrill(row.id)}
                aria-label={`Open engineers on ${row.name}`}
                className="grid w-full grid-cols-[auto_minmax(0,1.5fr)_auto_minmax(0,1.2fr)_auto] items-center gap-3 rounded-xl border border-transparent px-2.5 py-2.5 text-left transition hover:border-[rgba(11,59,46,0.16)] hover:bg-[rgba(11,59,46,0.04)] focus-visible:ring-2 focus-visible:ring-[rgba(51,146,116,0.5)]"
              >
                <span className="w-6 text-center text-sm font-extrabold tabular-nums" style={{ color: OMS_COLORS.caption }}>
                  {rows.indexOf(row) + 1}
                </span>

                <span className="min-w-0">
                  <motion.span layoutId={`plate-${row.id}`} className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-[13px] font-extrabold tracking-tight" style={{ color: OMS_COLORS.tealDark }}>
                      {row.name}
                    </span>
                  </motion.span>
                  <span className="mt-0.5 block truncate text-[10.5px] font-semibold" style={{ color: OMS_COLORS.caption }}>
                    {completionPct === null
                      ? 'No scheduled work'
                      : `${completionPct}% complete of ${formatNumber(row.scheduled)} tasks`}
                    {passPct !== null && ` · ${passPct}% pass on ${formatNumber(row.completed)} completed`}
                    {row.overdue > 0 && (
                      <span style={{ color: '#9F1239' }}> · {formatNumber(row.overdue)} tasks overdue</span>
                    )}
                  </span>
                </span>

                {/* Score block — divider separates it from the counts around it. */}
                <span className="border-l border-[rgba(11,59,46,0.12)] pl-3">
                  <ScoreGauge
                    score={row.score}
                    name={row.name}
                    size={40}
                    delta={row.delta}
                    deltaPhrase={deltaPhrase}
                    align="end"
                    showExplainer={false}
                  />
                </span>

                <span role="img" aria-label={`Task status mix for ${row.name}`} className="hidden sm:block">
                  <StatusBar counts={row.counts} height={10} />
                </span>

                <span className="flex items-center gap-1.5">
                  <Users className="h-3 w-3" style={{ color: OMS_COLORS.caption }} />
                  <StatCount label="Team" value={row.engineerCount} noun={row.engineerCount === 1 ? 'engineer' : 'engineers'} size="sm" />
                </span>
              </button>

              <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
                <label
                  className={`items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                    selected ? 'flex' : 'hidden group-focus-within:flex group-hover:flex'
                  }`}
                  style={{ color: OMS_COLORS.champagneDeep, backgroundColor: 'rgba(250,247,238,0.95)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleCompare(row.id)}
                    aria-label={`Compare ${row.name}`}
                    className="h-3 w-3 accent-[#264f3c]"
                  />
                  Compare
                </label>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenProject(row.id);
                  }}
                  aria-label={`Open project page for ${row.name}`}
                  title="Open project page"
                  className="hidden rounded-lg p-1.5 transition hover:bg-[rgba(11,59,46,0.08)] group-focus-within:block group-hover:block"
                  style={{ color: '#1F5A47', backgroundColor: 'rgba(250,247,238,0.95)' }}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </span>
            </motion.li>
          );
        })}
      </ul>
      {rows.length > PREVIEW_COUNT && !expanded && (
        <button
          type="button"
          onClick={onExpand}
          className="mt-2 w-full rounded-xl border border-[rgba(11,59,46,0.2)] py-2 text-[11px] font-extrabold uppercase tracking-[0.1em] transition hover:bg-[rgba(11,59,46,0.05)]"
          style={{ color: '#1F5A47', backgroundColor: '#fcfaf3' }}
        >
          View all {formatNumber(rows.length)} projects at {parentName} →
        </button>
      )}
    </div>
  );
}
